import { useContext, useEffect, useId, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, IconButton, Input, LinearProgress, Stack, Tooltip, Typography } from '@mui/joy';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { LayoutContext } from '../../context';
import { APIGetDraftsmanStatistics } from '../../api';

function day(value) {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
}
const number = (value, digits = 1) => value == null ? '—' : new Intl.NumberFormat('ru-RU', { maximumFractionDigits: digits }).format(Number(value));
const dateLabel = value => value ? value.split('-').reverse().join('.') : '—';

function Metric({ label, value, note, highlight }) {
  return <Box sx={{ minWidth: 0, p: 1.5, borderRadius: '16px',
    backgroundColor: highlight ? 'primary.softBg' : 'background.level1',
    border: '1px solid', borderColor: highlight ? 'primary.outlinedBorder' : 'transparent' }}>
    <Typography level="body-xs" sx={{ color: 'text.secondary', fontWeight: 600 }}>{label}</Typography>
    <Typography level="h3" component="p" sx={{ mt: 0.5, mb: 0.25, fontVariantNumeric: 'tabular-nums',
      color: highlight ? 'primary.softColor' : 'text.primary', letterSpacing: '-.04em' }}>{value}</Typography>
    <Typography level="body-xs" sx={{ color: 'text.tertiary', lineHeight: 1.35 }}>{note}</Typography>
  </Box>;
}

export default function PersonalStatistics({ refreshKey = 0 }) {
  const { tokenEmployee, account } = useContext(LayoutContext);
  const reducedMotion = useReducedMotion();
  const panelId = useId();
  const [from, setFrom] = useState(() => { const now = new Date(); return day(new Date(now.getFullYear(), now.getMonth(), 1)); });
  const [to, setTo] = useState(() => day(new Date()));
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const sequence = useRef(0);
  const applied = useRef({ date_from: from, date_to: to });
  useEffect(() => { setCollapsed(true); }, [account?.id]);
  const toggle = () => setCollapsed(value => !value);
  const load = async () => {
    if (!tokenEmployee || busy) return;
    if (!from || !to || from > to) { setError('Укажите корректный период'); return; }
    applied.current = { date_from: from, date_to: to };
    const version = ++sequence.current;
    setBusy(true); setError('');
    try {
      const result = await APIGetDraftsmanStatistics({ date_from: from, date_to: to }, tokenEmployee);
      if (version === sequence.current) setData(result);
    } catch (e) {
      if (version === sequence.current) {
        const detail = e?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Не удалось загрузить статистику. Попробуйте ещё раз.');
      }
    } finally { if (version === sequence.current) setBusy(false); }
  };
  useEffect(() => {
    setData(null); setBusy(false);
    // Смена сессии не должна показывать цифры предыдущего аккаунта.
    const version = ++sequence.current;
    if (tokenEmployee) {
      setBusy(true); setError('');
      APIGetDraftsmanStatistics(applied.current, tokenEmployee)
        .then(result => { if (sequence.current === version) setData(result); })
        .catch(() => { if (sequence.current === version) setError('Не удалось загрузить статистику. Попробуйте ещё раз.'); })
        .finally(() => { if (sequence.current === version) setBusy(false); });
    }
    return () => { sequence.current += 1; };
  }, [tokenEmployee, account?.id, refreshKey]);
  const range = data ? `${dateLabel(data.date_from)} — ${dateLabel(data.date_to)}` : 'Выберите период';
  const metrics = data ? [
    { label: 'Сдано мной', value: number(data.submitted), note: 'По дате сдачи чертежа, включая QA', highlight: true },
    { label: 'Объём сданного', value: `${number(data.submitted_square)} м²`, note: 'По сданным за период работам' },
    { label: 'Выдано менеджером', value: number(data.completed), note: `Объём выдачи: ${number(data.square)} м²` },
    { label: 'Взято в работу', value: number(data.assigned), note: `Из них сдано: ${number(data.cohort_submitted)}` },
    { label: 'Доля сдачи', value: data.submission_percent == null ? '—' : `${number(data.submission_percent)}%`, note: 'Среди взятых за выбранный период' },
    { label: 'Оценка качества', value: data.rating == null ? '—' : `${number(data.rating, 2)} / 5`, note: `Оценок: ${number(data.reviews_count)}` },
    { label: 'Возвраты качества', value: number(data.rework_events), note: 'Работы с возвратом за период' },
  ] : [];
  return <Box component="section" aria-label="Моя статистика" aria-busy={busy} sx={{
    my: 2, border: '1px solid', borderColor: 'divider', borderRadius: '20px', overflow: 'hidden',
    backgroundColor: 'background.surface', boxShadow: '0 4px 20px rgba(30,41,59,.035)',
  }}>
    <Stack direction="row" spacing={1.25} sx={{ px: 2, py: 1.5, alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
      <Box sx={{ display: 'grid', placeItems: 'center', width: 40, height: 40, borderRadius: '13px',
        color: 'primary.softColor', backgroundColor: 'primary.softBg', flexShrink: 0 }}><InsightsRoundedIcon /></Box>
      <Box sx={{ flex: 1, minWidth: 150 }}>
        <Typography level="title-md" component="h2" sx={{ fontWeight: 750 }}>Моя статистика</Typography>
        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{range}</Typography>
      </Box>
      {collapsed && data && <Chip size="sm" variant="soft" color="primary">Сдано: {number(data.submitted)} · {number(data.submitted_square)} м²</Chip>}
      <Tooltip title={collapsed ? 'Развернуть статистику' : 'Свернуть статистику'}>
        <IconButton aria-label={collapsed ? 'Развернуть статистику' : 'Свернуть статистику'}
          aria-expanded={!collapsed} aria-controls={panelId} onClick={toggle} variant="soft" size="sm">
          <ExpandMoreRoundedIcon sx={{ transform: collapsed ? 'rotate(0)' : 'rotate(180deg)', transition: reducedMotion ? 'none' : 'transform .2s' }} />
        </IconButton>
      </Tooltip>
    </Stack>
    {busy && <LinearProgress size="sm" aria-label="Загрузка статистики" />}
    {error && <Alert color="danger" role="alert" sx={{ mx: 2, mb: 1.5 }}>{error}</Alert>}
    <AnimatePresence initial={false}>
      {!collapsed && <Box component={motion.div} id={panelId}
        initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
        transition={{ duration: reducedMotion ? 0 : 0.2 }} sx={{ overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ px: 2, pb: 1.5, alignItems: { sm: 'center' } }}>
          <Input type="date" value={from} disabled={busy} onChange={e => setFrom(e.target.value)} size="sm"
            slotProps={{ input: { 'aria-label': 'Начало периода' } }} />
          <Typography level="body-xs" sx={{ display: { xs: 'none', sm: 'block' } }}>—</Typography>
          <Input type="date" value={to} disabled={busy} onChange={e => setTo(e.target.value)} size="sm"
            slotProps={{ input: { 'aria-label': 'Конец периода' } }} />
          <Button size="sm" loading={busy} onClick={() => load()} startDecorator={<RefreshRoundedIcon />}>Показать</Button>
          {data?.timezone && <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Часовой пояс: {data.timezone}</Typography>}
        </Stack>
        {!!data?.missing_finish_dates && <Alert color="warning" sx={{ mx: 2, mb: 2 }}>У {data.missing_finish_dates} завершённых работ не заполнена дата сдачи. Они не включены в периодную статистику; требуется проверить историю.</Alert>}
        {data && <Box sx={{ px: 2, pb: 2, display: 'grid', gap: 1,
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' } }}>
          {metrics.map(metric => <Metric key={metric.label} {...metric} />)}
        </Box>}
        {!data && !busy && !error && <Typography level="body-sm" sx={{ px: 2, pb: 2 }}>Выберите период и нажмите «Показать».</Typography>}
      </Box>}
    </AnimatePresence>
  </Box>;
}